import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveCumulative, completeExperiment } from '../../services/api';
import { CARD_REVISION } from '../../data/policyCard';
import { BANK_REVISION, CALIBRATION_BANK, familyOf, CalVariant, toFullContent, previewFor, timeFor, clockFor } from '../../data/calibrationBank';
import { SCOPE_ITEMS, KNOWLEDGE_ITEMS, QUIZ_ITEMS, CARD_ITEMS, PRE_FIELDS, POST_ITEMS, MAIN_INSTRUMENT_REVISION, ACTION_QUESTION, RECHECK_QUESTION, RECHECK_OPTIONS, RECHECK_LINES } from '../../data/mainArmMeasures';
import { CARD_PAGE_LEAD, FINISH_ERROR_TEXT, HOW_THIS_WORKS } from '../../data/studyText';
import { useExperiment } from '../../contexts/ExperimentContext';
import EmailDecisionBar from '../common/EmailDecisionBar';
import OutlookShell, { EmailMessage, ShellItem } from '../outlook/OutlookShell';
import StudyModal, { ConfidenceSlider } from '../study/StudyModal';
import StudyPage, { RadioQuestion, ScaleQuestion, StudyCard } from '../study/StudyPage';
import BrowserWindow from '../study/BrowserWindow';
import CardView, { CardPanel, CardStepper, HowThisWorks } from '../study/CardView';
import SaveToast, { useSaveState } from '../study/SaveToast';
import DebriefPage from '../study/DebriefPage';
import './MainTask.css';

/**
 * Main arm — verification-closure experiment (docs/MAIN_ARM_SPEC_2026-11-01.md), screens
 * rebuilt to docs/UI_ADVISOR_REVIEW_2026-11-02.md §3. Between-subjects interface pinned on
 * the participant:
 *   ordinary        — no guided check, commit enabled on arrival
 *   identity_plain  — guided check + identity question, NO verdict line
 *   identity        — guided check + system verdict line + identity question
 *   action          — guided check + request question (stored as `intent`), no verdict
 * Flow: pre → card → card check → how this works → 12 items → post → recheck → finish → debrief → complete.
 * Payload shape = backend/__tests__/fixtures/armFixtures.js mainPayload(iface).
 */

type Iface = 'ordinary' | 'identity_plain' | 'identity' | 'action';
const asksIdentity = (i: Iface) => i === 'identity' || i === 'identity_plain';
type FinalAction = 'commit' | 'decline' | 'ignore' | 'report' | 'declined_intent' | 'declined_identity';
type EventType = 'hover' | 'click' | 'step1' | 'step2' | 'dest' | 'reveal_close' | 'reveal_open' | 'identity' | 'intent' | 'verify_open' | 'verify_done' | 'back' | 'final' | 'conf';
interface Trial {
  familyId: number; classId: string; variantId: string; itemRevision: string; presentationOrder: number; shownAt: string;
  emailMs?: number; scrollDepth?: number; linkHovers?: number; linkHoverMs?: number; linkClickedAt?: string; cardOpensDuringItem?: number;
  step1?: { answer: string; truth: string; ms: number }; step2?: { answer: string; truth: string; ms: number };
  bannerShownAt?: string; bannerDismissedAt?: string; destinationShownAt?: string; revealOpenedAt?: string; revealMs?: number; revealCollapsedAt?: string;
  consequenceHoverMs?: number; revealCollapsed?: boolean;
  intent?: { answer: string; ms: number };
  identityQuestionShownAt?: string; identity?: { answer: string; ms: number };
  verifyUsed?: boolean; verifyChannelShownAt?: string; verifyMs?: number;
  lastStep?: string;
  reentries?: number;
  eventLog?: Array<{ t: string; type: EventType }>;
  finalAction?: FinalAction; finalAt?: string; decisionMs?: number; confidence?: number; confidenceMs?: number; confidenceTouched?: boolean;
}
type View = 'email' | 'guided' | 'destination' | 'confidence' | 'verify';
/** Recheck page (RQ-fit review): one "should this request have gone ahead?" judgement per A and L request handled. */
interface Recheck {
  shownAt: string; order: string[]; cardOpenedAt?: string; submittedAt?: string; score?: number;
  items?: Array<{ variantId: string; familyId: number; classId: string; answer: string; correct: boolean }>;
}
interface CardOpen { at: string; itemIndex: number; closedAt?: string; phase: View }
interface Backup {
  participantId: string; interface: Iface; rotation: number; assignment: string[];
  pre?: any; cardFirstShownAt?: string; cardReadMs?: number; cardComprehension?: any; cardOpenLog: CardOpen[];
  revisits: Array<{ variantId: string; at: string }>; debrief?: { shownAt?: string; acknowledgedAt?: string };
  trials: Trial[]; post?: any; recheck?: Recheck; startedAt: string; finished?: boolean; completedAt?: string; completedOk?: boolean; howShownAt?: string;
}

// ---- assignment: 6 A / 3 S / 3 L via a 4-rotation Latin design over 12 families ----
const PATTERN = ['A', 'A', 'S', 'L']; // applied cyclically after rotation → 6A/3S/3L
function hashString(s: string): number { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }
function mulberry32(seed: number) { return () => { seed |= 0; seed = (seed + 0x6D2B79F5) | 0; let t = Math.imul(seed ^ (seed >>> 15), 1 | seed); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }
function shuffle<T>(arr: T[], rng: () => number): T[] { const a = arr.slice(); for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; }
/**
 * Rotation is server-assigned in balanced interface × rotation blocks (advisor R3) and passed
 * in; the participant-id hash is only the fallback. Presentation order stays seeded from the
 * participant id, so a refresh never reshuffles.
 */
export function buildMainAssignment(participantId: string, serverRotation?: number | null): { rotation: number; items: CalVariant[] } {
  const seed = hashString(participantId);
  const rotation = typeof serverRotation === 'number' && serverRotation >= 0 && serverRotation < 4 ? serverRotation : seed % 4;
  const rng = mulberry32(seed);
  const items = CALIBRATION_BANK.map((f, i) => {
    const cls = PATTERN[(i + rotation) % 4] as 'A' | 'S' | 'L';
    const v = f.variants.find(x => x.classId === cls); if (!v) throw new Error(`no ${cls} for family ${f.familyId}`); return v;
  });
  return { rotation, items: shuffle(items, rng) };
}
/** Seeded order of the recheck lines (A and L items the participant handled), recorded as recheck.order. */
export function buildRecheckOrder(participantId: string, items: CalVariant[]): string[] {
  return shuffle(items.filter(v => v.classId !== 'S').map(v => v.variantId), mulberry32(hashString(participantId) ^ 0x85ebca6b));
}
/** Seeded order of the eight pre-task quiz items (advisor M1), recorded as pre.itemOrder. */
export function buildQuizOrder(participantId: string): string[] {
  return shuffle(QUIZ_ITEMS.map(i => i.id), mulberry32(hashString(participantId) ^ 0x9e3779b9));
}

// True for university.edu and any *.university.edu host, for either an email address or a URL.
const hostOf = (addrOrUrl: string) => (addrOrUrl.includes('@') ? addrOrUrl.split('@').pop() || '' : addrOrUrl.replace(/^https?:\/\//, '').split('/')[0]);
const isUniversity = (addrOrUrl: string) => /(^|\.)university\.edu$/i.test(hostOf(addrOrUrl));

/** Consequence block for a variant (bank §3 destination spec), by family and class. */
function consequenceFor(v: CalVariant): { what: string; forWhom: string; allows: string; happens: string } {
  const f = familyOf(v.familyId);
  const opt = f.authorizeOptions.find(o => o.key === v.intendedAuthorize)?.label || '';
  const objects: Record<number, [string, string]> = {
    1: ['Sign-in approval', v.classId === 'A' ? 'Windows PC · Denver, CO · now' : 'Laptop (macOS) · Fort Collins · 2:05 pm'],
    2: ['Direct deposit account', v.classId === 'A' ? 'Account ending 7731' : 'Account ending 2208'],
    3: ['Supply order', v.classId === 'A' ? 'Summit Supply · payee details from the attachment' : 'Front Range Office Supply · standing order'],
    4: ['File share: Fall Events Budget.xlsx', v.classId === 'A' ? 'j.ramos.events@gmail.com' : 'maya.chen@university.edu'],
    5: ['App connection', v.classId === 'A' ? 'StudyBuddy — read grades, read and send email, access files' : 'University Mobile — read schedule and grades, send notifications'],
    6: ['Timesheet, Nov 3–14, 10 hours', v.classId === 'A' ? 'Maya Chen (filed under your login)' : 'You'],
    7: ['Password reset', v.classId === 'A' ? 'Windows PC · Denver · 4:12 pm' : 'Laptop · Fort Collins · 9:00 am'],
    8: ['Sign-in app device', v.classId === 'A' ? 'Android phone, activated today' : 'iPhone, activated Monday'],
    9: ['Calendar delegate access', v.classId === 'A' ? 'Tyler (new office assistant)' : 'Dana Ortiz'],
    10: ['Software install', v.classId === 'A' ? 'Remote Support Tool — full remote control of your laptop' : 'University VPN — connects your laptop to campus systems'],
    11: ['Spring schedule change', v.classId === 'A' ? 'Drop BUS 150' : 'Add BUS 160'],
    12: ['Spring housing request', v.classId === 'A' ? 'Move to Braiden Hall' : 'Renew Parmelee Hall 214'],
  };
  const [what, forWhom] = objects[v.familyId];
  // RQ-fit review: the destination states facts, never the card's rule. The calibration option
  // label for F04-A says "An outside gmail address…"; the main arm drops the evaluative word.
  // F03-A's calibration label says "new payee details" (a red flag the email never used).
  const MAIN_ALLOWS: Record<string, string> = {
    'F04-A': 'j.ramos.events@gmail.com can open the budget file',
    'F03-A': 'An order to Summit Supply, with the payee details from the attachment, goes to Dana for approval',
  };
  const allows = MAIN_ALLOWS[v.variantId] || opt;
  return { what, forWhom, allows, happens: `Clicking "${f.commitLabel}" does this immediately.` };
}

/** Verify channel frame (advisor §3.5.4), chosen by the channel label. */
function VerifyChannel({ label, reply }: { label: string; reply: string }) {
  if (/^Open the Sign-in app/i.test(label)) return (
    <div className="vc-wrap"><div className="vc-phone"><div className="vc-phone-screen">
      <div className="vc-phone-app">Sign-in app</div>
      <div className="vc-phone-card">{reply}</div>
    </div></div></div>
  );
  if (/^Call /i.test(label)) {
    const m = label.match(/^Call (\w+) at ([\d-]+)/i);
    const quote = reply.replace(/^\w+:\s*/, '').replace(/^"|"$/g, '');
    return (
      <div className="vc-wrap"><div className="vc-call">
        <div className="vc-call-name">Calling {m ? (m[1] === 'Dana' ? 'Dana Ortiz' : m[1]) : 'Dana Ortiz'}…</div>
        <div className="vc-call-num">{m ? m[2] : ''}</div>
        <div className="vc-bubble">{quote}</div>
      </div></div>
    );
  }
  if (/^Text /i.test(label)) {
    const who = (label.match(/^Text (\w+)/i) || [])[1] || 'Maya';
    const quote = reply.replace(/^\w+:\s*/, '').replace(/^"|"$/g, '');
    return (
      <div className="vc-wrap"><div className="vc-text">
        <div className="vc-text-head">{who}</div>
        <div className="out">Hey — did you just email me?</div>
        <div className="in">{quote}</div>
      </div></div>
    );
  }
  const section = (label.match(/open (.+)$/i) || [])[1] || 'Account';
  const [head, ...rest] = reply.split(' — ');
  return (
    <div className="vc-wrap"><div className="vc-portal">
      <div className="vc-portal-head">{rest.length ? head : section}</div>
      <div className="vc-portal-body">{rest.length ? rest.join(' — ') : reply}</div>
    </div></div>
  );
}

const MainTask: React.FC = () => {
  const navigate = useNavigate();
  const { experimentData } = useExperiment();
  const participantId = localStorage.getItem('participantId') || '';
  const iface: Iface = (experimentData?.participant?.mainArmInterface as Iface) || (localStorage.getItem('mainArmInterface') as Iface) || 'ordinary';
  const backupKey = `mainBackup_${participantId}`;
  const serverRotation = useMemo(() => {
    const r = experimentData?.participant?.rotation ?? Number(localStorage.getItem('assignedRotation') ?? 'x');
    return Number.isInteger(r) ? (r as number) : null;
  }, [experimentData]);
  const assignment = useMemo(() => buildMainAssignment(participantId || 'anonymous', serverRotation), [participantId, serverRotation]);
  const quizOrder = useMemo(() => buildQuizOrder(participantId || 'anonymous'), [participantId]);

  const [backup, setBackup] = useState<Backup>(() => {
    try { const raw = localStorage.getItem(backupKey); if (raw) { const b = JSON.parse(raw) as Backup; if (b.participantId === participantId) return { ...b, revisits: b.revisits || [], cardOpenLog: b.cardOpenLog || [] }; } } catch { /* ignore */ }
    return { participantId, interface: iface, rotation: assignment.rotation, assignment: assignment.items.map(i => i.variantId), cardOpenLog: [], revisits: [], trials: [], startedAt: new Date().toISOString() };
  });
  const backupRef = useRef(backup); backupRef.current = backup;
  const update = (b: Backup) => { setBackup(b); backupRef.current = b; return b; };
  const finishedCount = backup.trials.filter(t => t.finalAction && t.confidence != null).length;
  type Phase = 'pre' | 'card' | 'cardcheck' | 'how' | 'item' | 'post' | 'recheck' | 'finish' | 'debrief';
  const initialPhase = (): Phase => {
    if (backup.completedOk) return 'debrief';
    if (!backup.pre) return 'pre'; if (!backup.cardFirstShownAt) return 'card'; if (!backup.cardComprehension) return 'cardcheck';
    if (!backup.howShownAt && finishedCount === 0) return 'how';
    if (finishedCount < assignment.items.length) return 'item'; if (!backup.post) return 'post'; if (!backup.recheck?.submittedAt) return 'recheck'; return 'finish';
  };
  const [phase, setPhase] = useState<Phase>(initialPhase);
  const [index, setIndex] = useState(Math.min(finishedCount, assignment.items.length - 1));
  const [view, setView] = useState<View>('email');
  const [guidedStep, setGuidedStep] = useState<1 | 2>(1);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [cardOpen, setCardOpen] = useState(false);
  const [hoverUrl, setHoverUrl] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [revealOpen, setRevealOpen] = useState(false);
  const [intentAnswer, setIntentAnswer] = useState<'' | 'yes' | 'no'>('');
  const [identityAnswer, setIdentityAnswer] = useState<'' | 'yes' | 'no'>('');
  const [confidence, setConfidence] = useState(50);
  const [confTouched, setConfTouched] = useState(false);
  const [saveState, trackSave] = useSaveState();
  const [finishing, setFinishing] = useState(false); const [finishError, setFinishError] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const t = useRef<Trial | null>(null); // current trial accumulator
  const timers = useRef({ itemShown: 0, cardShown: 0, step: 0, dest: 0, reveal: 0, conf: 0, hovers: 0, hoverMs: 0, hoverStart: 0, consHover: 0, consStart: 0, cardOpens: 0, scroll: 0, verify: 0 });

  const current = assignment.items[index]; const family = current ? familyOf(current.familyId) : null;
  const viewing: CalVariant | undefined = viewingId ? assignment.items.find(v => v.variantId === viewingId) : current;
  const fullContent = useMemo(() => (viewing ? toFullContent(viewing) : ''), [viewing]);

  const showToast = (text: string, ms = 2500) => { setToast(text); if (toastTimer.current) clearTimeout(toastTimer.current); toastTimer.current = setTimeout(() => setToast(null), ms); };
  const writeLocal = (b: Backup) => { try { localStorage.setItem(backupKey, JSON.stringify(b)); } catch { /* ignore */ } };
  const payloadOf = (b: Backup) => ({
    interface: b.interface, bankRevision: BANK_REVISION, cardRevision: CARD_REVISION, instrumentRevision: MAIN_INSTRUMENT_REVISION, rotation: b.rotation, assignment: b.assignment, pre: b.pre,
    cardFirstShownAt: b.cardFirstShownAt, cardReadMs: b.cardReadMs, cardComprehension: b.cardComprehension, cardOpenLog: b.cardOpenLog,
    revisits: b.revisits, debrief: b.debrief, trials: b.trials, post: b.post, recheck: b.recheck,
    startedAt: b.startedAt, completedAt: b.finished ? b.completedAt : undefined,
  });
  const persist = useCallback(async (b: Backup): Promise<boolean> => {
    writeLocal(b);
    return trackSave((async () => { try { await saveCumulative(participantId, 'main-task', payloadOf(b)); return true; } catch { return false; } })());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backupKey, participantId]);

  /** Spec §3: persist partial trial state on every step event so a refresh mid-trial loses nothing. */
  /** Spontaneous search counters (M9): counted until the first link click, then frozen. */
  const syncHovers = () => { if (!t.current || t.current.linkClickedAt) return; t.current.linkHovers = timers.current.hovers; t.current.linkHoverMs = timers.current.hoverMs; };
  /** Local-only write of the current trial so a refresh keeps hover counters that precede any step event. */
  const writeLocalTrial = () => { if (!t.current) return; const b = backupRef.current; writeLocal(update({ ...b, trials: b.trials.filter(x => x.variantId !== t.current!.variantId).concat({ ...t.current }) })); };
  const persistPartial = useCallback((step: string) => {
    if (!t.current) return;
    syncHovers();
    t.current.lastStep = step;
    const b = backupRef.current;
    persist(update({ ...b, trials: b.trials.filter(x => x.variantId !== t.current!.variantId).concat({ ...t.current }) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persist]);
  /** Ordered step events (advisor §5.4). */
  const ev = (type: EventType, at = Date.now()) => { if (!t.current) return; (t.current.eventLog = t.current.eventLog || []).push({ t: new Date(at).toISOString(), type }); };

  const startItem = useCallback(() => {
    if (!current) return;
    const now = Date.now();
    timers.current = { itemShown: now, cardShown: 0, step: 0, dest: 0, reveal: 0, conf: 0, hovers: 0, hoverMs: 0, hoverStart: 0, consHover: 0, consStart: 0, cardOpens: 0, scroll: 0, verify: 0 };
    setViewingId(null); setHoverUrl(null); setConfidence(50); setConfTouched(false);
    const partial = backupRef.current.trials.find(x => x.variantId === current.variantId && !(x.finalAction && x.confidence != null));
    if (partial) {
      // Resume the in-progress trial at its last persisted step (spec §3).
      t.current = { ...partial, eventLog: (partial.eventLog || []).slice() };
      timers.current.itemShown = new Date(partial.shownAt).getTime() || now;
      timers.current.hovers = partial.linkHovers || 0; timers.current.hoverMs = partial.linkHoverMs || 0; timers.current.consHover = partial.consequenceHoverMs || 0; timers.current.cardOpens = partial.cardOpensDuringItem || 0;
      const ls = partial.lastStep || '';
      setIntentAnswer((partial.intent?.answer as any) || ''); setIdentityAnswer((partial.identity?.answer as any) || '');
      if (partial.finalAction) {
        // Review B1: the decision is already taken and persisted; only the confidence rating is
        // outstanding. Never re-show the destination or re-enable commit.
        timers.current.conf = now; setRevealOpen(false); setView('confidence'); return;
      }
      if (ls === 'verify_open') { timers.current.verify = now; setView('verify'); }
      else if (ls === 'verify_done') { setView('email'); }
      else if (ls === 'step1') { timers.current.step = now; setGuidedStep(2); setView('guided'); }
      else if (ls === 'link' && iface !== 'ordinary') { timers.current.step = now; setGuidedStep(1); setView('guided'); }
      else if (['link', 'step2', 'identity', 'destination', 'reveal_open', 'reveal_close', 'intent'].includes(ls)) {
        timers.current.dest = now; const open = !!partial.revealOpenedAt && !partial.revealCollapsedAt; timers.current.reveal = open ? now : 0; setRevealOpen(open); setView('destination');
        if (ls !== 'intent') setIntentAnswer(''); if (ls !== 'identity') setIdentityAnswer('');
      }
      else { setView('email'); setGuidedStep(1); setRevealOpen(false); }
      return;
    }
    t.current = { familyId: current.familyId, classId: current.classId, variantId: current.variantId, itemRevision: BANK_REVISION, presentationOrder: index + 1, shownAt: new Date(now).toISOString(), linkHovers: 0, linkHoverMs: 0, cardOpensDuringItem: 0, scrollDepth: 0, verifyUsed: false, reentries: 0, eventLog: [] };
    setView('email'); setGuidedStep(1); setRevealOpen(false); setIntentAnswer(''); setIdentityAnswer('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, index]);
  useEffect(() => { if (phase === 'item') startItem(); }, [phase, index, startItem]);

  // ---- pre ----
  const [pre, setPre] = useState<Record<string, string>>({});
  const preMissing = QUIZ_ITEMS.filter(i => !pre[i.id]).length + Object.keys(PRE_FIELDS).filter(k => !pre[k]).length;
  const submitPre = () => {
    if (preMissing) return;
    const scopeItems = SCOPE_ITEMS.map(i => ({ id: i.id, answer: pre[i.id], correct: pre[i.id] === i.correct }));
    const knowItems = KNOWLEDGE_ITEMS.map(i => ({ id: i.id, answer: pre[i.id], correct: pre[i.id] === i.correct }));
    const b = update({ ...backup, pre: {
      scope: { items: scopeItems, score: scopeItems.filter(i => i.correct).length }, knowledge: { items: knowItems, score: knowItems.filter(i => i.correct).length },
      itemOrder: quizOrder,
      training: pre.training, simulations: pre.simulations, signinApp: pre.signinApp, signinFreq: pre.signinFreq, year: pre.year, major: pre.major, ageBand: pre.ageBand, emailsPerDay: pre.emailsPerDay,
      screen: { w: window.innerWidth, h: window.innerHeight }, device: /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop', submittedAt: new Date().toISOString() } });
    persist(b); timers.current.cardShown = Date.now(); setPhase('card');
  };
  // ---- card, card check, how this works ----
  const cardRead = () => { const b = update({ ...backup, cardFirstShownAt: backup.cardFirstShownAt || new Date().toISOString(), cardReadMs: backup.cardReadMs ?? (Date.now() - (timers.current.cardShown || Date.now())) }); persist(b); setPhase('cardcheck'); };
  const [cardAns, setCardAns] = useState<Record<string, string>>({});
  const submitCardCheck = () => {
    if (!CARD_ITEMS.every(i => cardAns[i.id])) return;
    const items = CARD_ITEMS.map(i => ({ id: i.id, answer: cardAns[i.id], correct: cardAns[i.id] === i.correct }));
    const b = update({ ...backup, cardComprehension: { items, correct: items.filter(i => i.correct).length, submittedAt: new Date().toISOString() } });
    persist(b); setPhase('how');
  };
  const startItems = () => { writeLocal(update({ ...backupRef.current, howShownAt: new Date().toISOString() })); setPhase('item'); };
  const logCardOpen = () => {
    timers.current.cardOpens += 1;
    writeLocal(update({ ...backupRef.current, cardOpenLog: [...backupRef.current.cardOpenLog, { at: new Date().toISOString(), itemIndex: index, phase: view }] }));
  };
  const logCardClose = () => {
    const log = backupRef.current.cardOpenLog; if (!log.length || log[log.length - 1].closedAt) return;
    writeLocal(update({ ...backupRef.current, cardOpenLog: log.map((e, i) => (i === log.length - 1 ? { ...e, closedAt: new Date().toISOString() } : e)) }));
  };
  const toggleCard = () => { if (cardOpen) { logCardClose(); setCardOpen(false); } else { logCardOpen(); setCardOpen(true); } };

  // ---- email view handlers (spontaneous search on the pending message only) ----
  const onLinkEnter = (url: string) => {
    setHoverUrl(url);
    if (view !== 'email' || viewingId || !t.current) return;
    ev('hover'); // every hover is in the event log; the counters below stop at the first click (M9)
    if (t.current.linkClickedAt) return;
    timers.current.hovers += 1; timers.current.hoverStart = Date.now();
  };
  const closeHover = (now: number) => { if (timers.current.hoverStart) { timers.current.hoverMs += now - timers.current.hoverStart; timers.current.hoverStart = 0; } syncHovers(); };
  const onLinkLeave = () => {
    setHoverUrl(null);
    if (timers.current.hoverStart) { closeHover(Date.now()); writeLocalTrial(); }
  };
  const onScroll = (e: React.UIEvent<HTMLDivElement>) => { const el = e.currentTarget; const d = el.scrollHeight > el.clientHeight ? (el.scrollTop + el.clientHeight) / el.scrollHeight : 1; timers.current.scroll = Math.max(timers.current.scroll, Math.min(1, d)); };
  const onLinkClick = () => {
    if (view !== 'email' || viewingId || !t.current) return;
    const now = Date.now(); closeHover(now);
    if (t.current.linkClickedAt) t.current.reentries = (t.current.reentries || 0) + 1; // review B2: reopening is a supported, logged state
    t.current.linkClickedAt = new Date(now).toISOString(); t.current.emailMs = t.current.emailMs || (now - timers.current.itemShown);
    t.current.scrollDepth = Math.round(timers.current.scroll * 100) / 100; // hover counters were synced by closeHover and are now frozen
    ev('click', now);
    setHoverUrl(null); setIntentAnswer(''); setIdentityAnswer('');
    if (iface === 'ordinary') { arriveDestination(now); }
    else { timers.current.step = now; setGuidedStep(1); setView('guided'); persistPartial('link'); }
  };
  /**
   * Destination page: identical in all arms, consequence block expanded by default. Both guided
   * arms ask their terminal question HERE, above the commit button. In the identity arm the
   * verdict line renders with the page, so bannerShownAt = destination render.
   */
  const arriveDestination = (now: number) => {
    if (!t.current) return;
    const iso = new Date(now).toISOString();
    timers.current.dest = now; t.current.destinationShownAt = iso;
    setRevealOpen(true); t.current.revealOpenedAt = t.current.revealOpenedAt || iso; timers.current.reveal = now;
    t.current.consequenceHoverMs = t.current.consequenceHoverMs || 0; t.current.revealCollapsed = t.current.revealCollapsed || false;
    if (asksIdentity(iface)) t.current.identityQuestionShownAt = iso;
    if (iface === 'identity') t.current.bannerShownAt = iso; // verdict line exists only in `identity`
    ev('dest', now);
    setView('destination'); persistPartial('destination');
  };
  // ---- guided steps ----
  const answerStep = (answer: 'yes' | 'no') => {
    if (!t.current || !current) return; const now = Date.now(); const ms = now - timers.current.step;
    if (guidedStep === 1) { t.current.step1 = { answer, truth: isUniversity(current.senderAddress) ? 'yes' : 'no', ms }; timers.current.step = now; ev('step1', now); setGuidedStep(2); persistPartial('step1'); return; }
    t.current.step2 = { answer, truth: isUniversity(current.linkUrl) ? 'yes' : 'no', ms }; ev('step2', now);
    arriveDestination(now);
  };
  const answerIdentity = (a: 'yes' | 'no') => {
    if (!t.current) return; const now = Date.now();
    t.current.identity = { answer: a, ms: now - timers.current.dest };
    if (iface === 'identity') t.current.bannerDismissedAt = new Date(now).toISOString();
    ev('identity', now); setIdentityAnswer(a); if (a === 'no') finalize('declined_identity'); else persistPartial('identity');
  };
  const answerIntent = (a: 'yes' | 'no') => { if (!t.current) return; const now = Date.now(); t.current.intent = { answer: a, ms: now - timers.current.dest }; ev('intent', now); setIntentAnswer(a); if (a === 'no') finalize('declined_intent'); else persistPartial('intent'); };
  // ---- destination: consequence block ----
  const closeCons = (now: number) => { if (timers.current.consStart) { timers.current.consHover += now - timers.current.consStart; timers.current.consStart = 0; } if (t.current) t.current.consequenceHoverMs = timers.current.consHover; };
  const toggleReveal = () => {
    if (!t.current) return; const now = Date.now();
    if (revealOpen) { closeCons(now); t.current.revealMs = (t.current.revealMs || 0) + (timers.current.reveal ? now - timers.current.reveal : 0); t.current.revealCollapsedAt = new Date(now).toISOString(); t.current.revealCollapsed = true; timers.current.reveal = 0; ev('reveal_close', now); setRevealOpen(false); persistPartial('reveal_close'); }
    else { t.current.revealOpenedAt = t.current.revealOpenedAt || new Date(now).toISOString(); timers.current.reveal = now; ev('reveal_open', now); setRevealOpen(true); persistPartial('reveal_open'); }
  };
  /** Verify is a channel, not an exit (spec §3): open the independent check, then return for a final action. */
  const openVerify = () => { if (!t.current) return; const now = Date.now(); closeHover(now); t.current.verifyUsed = true; t.current.verifyChannelShownAt = t.current.verifyChannelShownAt || new Date(now).toISOString(); timers.current.verify = now; ev('verify_open', now); setHoverUrl(null); setView('verify'); persistPartial('verify_open'); };
  const closeVerify = () => { if (!t.current) return; const now = Date.now(); t.current.verifyMs = (t.current.verifyMs || 0) + (timers.current.verify ? now - timers.current.verify : 0); timers.current.verify = 0; ev('verify_done', now); setView('email'); persistPartial('verify_done'); };
  const finalize = (action: FinalAction) => {
    if (!t.current) return; const now = Date.now();
    closeHover(now); if (t.current.destinationShownAt) closeCons(now);
    if (t.current.revealOpenedAt && timers.current.reveal) { t.current.revealMs = (t.current.revealMs || 0) + (now - timers.current.reveal); timers.current.reveal = 0; }
    if (!t.current.emailMs) { t.current.emailMs = now - timers.current.itemShown; t.current.scrollDepth = Math.round(timers.current.scroll * 100) / 100; }
    t.current.finalAction = action; t.current.finalAt = new Date(now).toISOString(); t.current.decisionMs = now - timers.current.itemShown; t.current.cardOpensDuringItem = timers.current.cardOpens;
    ev('final', now);
    if (cardOpen) { logCardClose(); setCardOpen(false); }
    // Review B1: the decision is persisted (backup + server) BEFORE the confidence modal renders.
    persistPartial('final');
    if (toastTimer.current) clearTimeout(toastTimer.current);
    timers.current.conf = now; setToast(null); setHoverUrl(null); setConfidence(50); setConfTouched(false); setView('confidence');
  };
  const backToInbox = () => {
    const now = Date.now();
    if (t.current && revealOpen && timers.current.reveal) { closeCons(now); t.current.revealMs = (t.current.revealMs || 0) + (now - timers.current.reveal); timers.current.reveal = 0; }
    ev('back', now);
    // Review B2: leaving the link flow clears every terminal answer so a reopened link can be
    // checked and answered again (the trial is not final until a decision-bar action or a commit).
    setView('email'); setRevealOpen(false); setIntentAnswer(''); setIdentityAnswer(''); setGuidedStep(1); persistPartial('back');
  };
  const submitConfidence = async () => {
    if (!t.current || !confTouched) return;
    const now = Date.now();
    t.current.confidence = confidence; t.current.confidenceMs = now - timers.current.conf; t.current.confidenceTouched = confTouched; ev('conf', now);
    const b = backupRef.current;
    await persist(update({ ...b, trials: b.trials.filter(x => x.variantId !== t.current!.variantId).concat(t.current) }));
    if (index + 1 >= assignment.items.length) setPhase('post'); else setIndex(index + 1);
  };
  // ---- revisits (M6) ----
  const selectMessage = (id: string) => {
    if (!current) return;
    if (view !== 'email') { showToast('Use Back to inbox to return to your messages first'); return; }
    if (id === current.variantId) { setViewingId(null); setHoverUrl(null); return; }
    persist(update({ ...backupRef.current, revisits: [...backupRef.current.revisits, { variantId: id, at: new Date().toISOString() }] }));
    setViewingId(id); setHoverUrl(null);
  };
  // ---- post ----
  const [post, setPost] = useState<Record<string, any>>({});
  const postItems = POST_ITEMS.filter(it => !it.arms || it.arms.includes(iface));
  const postMissing = postItems.filter(it => !post[it.id]).length + (post.verifiedMeaning === 'other' && !post.verifiedMeaningOther ? 1 : 0);
  const submitPost = () => {
    if (postMissing) return;
    const guided = iface !== 'ordinary';
    const b = update({ ...backup, post: {
      mcGuided: post.mcGuided, mcTerminal: post.mcTerminal, manipulationNoticed: post.manipulationNoticed,
      verifiedMeaning: post.verifiedMeaning, verifiedMeaningOther: post.verifiedMeaning === 'other' ? (post.verifiedMeaningOther || '') : '',
      closure: Number(post.closure), closureFelt: guided ? Number(post.closureFelt) : null, verdictTrust: iface === 'identity' ? Number(post.verdictTrust) : null,
      riskSeriousness: Number(post.riskSeriousness), attention: post.attention,
      demandText: post.demandText || '', suspectedTest: post.suspectedTest, seriousness: Number(post.seriousness), realism: Number(post.realism), receivedSimilar: post.receivedSimilar,
      cardUseSelf: null, submittedAt: new Date().toISOString() } });
    persist(b); setPhase('recheck');
  };
  // ---- recheck (after post, so the manipulation checks and CL1 are not primed by it) ----
  const recheckOrder = useMemo(() => buildRecheckOrder(participantId || 'anonymous', assignment.items), [participantId, assignment]);
  // Draft answers survive a refresh (advisor re-check #11); they never enter the saved payload.
  const draftKey = `mainRecheckDraft_${participantId}`;
  const [recheckAns, setRecheckAnsRaw] = useState<Record<string, string>>(() => { try { return JSON.parse(localStorage.getItem(draftKey) || '{}') || {}; } catch { return {}; } });
  const setRecheckAns = (f: (r: Record<string, string>) => Record<string, string>) => setRecheckAnsRaw(r => { const n = f(r); try { localStorage.setItem(draftKey, JSON.stringify(n)); } catch { /* ignore */ } return n; });
  useEffect(() => {
    if (phase !== 'recheck' || backupRef.current.recheck?.shownAt) return;
    persist(update({ ...backupRef.current, recheck: { shownAt: new Date().toISOString(), order: recheckOrder } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);
  const recheckMissing = recheckOrder.filter(id => !recheckAns[id]).length;
  const onRecheckCard = (open: boolean) => {
    const b = backupRef.current; if (!open || b.recheck?.cardOpenedAt) return;
    writeLocal(update({ ...b, recheck: { ...(b.recheck || { shownAt: new Date().toISOString(), order: recheckOrder }), cardOpenedAt: new Date().toISOString() } }));
  };
  const submitRecheck = () => {
    if (recheckMissing) return;
    const items = recheckOrder.map(id => { const v = assignment.items.find(x => x.variantId === id)!; const answer = recheckAns[id];
      return { variantId: id, familyId: v.familyId, classId: v.classId, answer, correct: answer === (v.classId === 'A' ? 'no' : 'yes') }; });
    const prev = backupRef.current.recheck || { shownAt: new Date().toISOString(), order: recheckOrder };
    persist(update({ ...backupRef.current, recheck: { ...prev, order: recheckOrder, items, score: items.filter(i => i.correct).length, submittedAt: new Date().toISOString() } }));
    try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
    setPhase('finish');
  };
  // ---- finish → debrief → complete ----
  const finish = useCallback(async () => {
    setFinishing(true); setFinishError(false);
    await persist(update({ ...backupRef.current, finished: true, completedAt: backupRef.current.completedAt || new Date().toISOString() }));
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const result = await completeExperiment(participantId, { arm: 'main', interface: iface, bankRevision: BANK_REVISION, cardRevision: CARD_REVISION });
        try {
          if (result?.completionUrl) localStorage.setItem('prolificCompletionUrl', result.completionUrl); else localStorage.removeItem('prolificCompletionUrl');
          if (result?.completionCode) localStorage.setItem('prolificCompletionCode', result.completionCode);
          localStorage.setItem('completionPlatform', result?.completionPlatform || ''); localStorage.removeItem('completionFailed');
        } catch { /* ignore */ }
        writeLocal(update({ ...backupRef.current, completedOk: true }));
        setFinishing(false); setPhase('debrief'); return;
      } catch { await new Promise(r => setTimeout(r, 1500 * (attempt + 1))); }
    }
    setFinishing(false); setFinishError(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iface, participantId, persist]);
  useEffect(() => { if (phase === 'finish' && !finishing && !finishError) finish(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);
  const debriefShown = useCallback(() => {
    const b = backupRef.current; if (b.debrief?.shownAt) return;
    persist(update({ ...b, debrief: { ...(b.debrief || {}), shownAt: new Date().toISOString() } }));
  }, [persist]);
  const debriefAck = async () => {
    const b = backupRef.current;
    await persist(update({ ...b, debrief: { ...(b.debrief || {}), acknowledgedAt: new Date().toISOString() } }));
    try { localStorage.removeItem(backupKey); } catch { /* ignore */ }
    navigate('/complete');
  };

  // ================= render =================
  if (!participantId) return <div className="cal-wrap"><p>Session not found. Please return to your SONA link and start again.</p></div>;

  if (phase === 'pre') return (
    <StudyPage title="Before you start" lead="A few questions about you and about university email. Answer as best you can." progress="Step 1 of 4 · about 3 minutes"
      formTestId="pre-form" submitTestId="pre-submit" primaryLabel="Continue" canSubmit={!preMissing} remaining={preMissing} onSubmit={submitPre}>
      <div className="study-section">Your background</div>
      {Object.entries(PRE_FIELDS).map(([k, f]) => (
        <RadioQuestion key={k} name={k} text={f.text} options={f.options.map(([key, label]) => ({ key, label }))} value={pre[k]} onChange={v => setPre(p => ({ ...p, [k]: v }))} />
      ))}
      <div className="study-section">Quick check</div>
      {quizOrder.map((id, n) => { const it = QUIZ_ITEMS.find(q => q.id === id)!; return (
        <RadioQuestion key={id} name={id} number={n + 1} text={it.text} options={it.options} value={pre[id]} onChange={v => setPre(p => ({ ...p, [id]: v }))} />
      ); })}
    </StudyPage>
  );

  if (phase === 'card') return (
    <StudyPage title="Who you are in this study" progress="Step 2 of 4 · about 2 minutes">
      <CardStepper intro={<>{CARD_PAGE_LEAD}<b>My job and systems</b>.</>} doneLabel="Yes, I have finished reading — continue" onDone={cardRead} />
    </StudyPage>
  );

  if (phase === 'cardcheck') return (
    <StudyPage title="Two questions about your role" progress="Step 2 of 4" formTestId="cardcheck-form" submitTestId="cardcheck-submit" primaryLabel="Continue"
      canSubmit={CARD_ITEMS.every(i => cardAns[i.id])} remaining={CARD_ITEMS.filter(i => !cardAns[i.id]).length} onSubmit={submitCardCheck}>
      {CARD_ITEMS.map(it => <RadioQuestion key={it.id} name={it.id} text={it.text} options={it.options} value={cardAns[it.id]} onChange={v => setCardAns(a => ({ ...a, [it.id]: v }))} />)}
    </StudyPage>
  );

  if (phase === 'how') return (
    <StudyPage title="How this works" progress="Step 2 of 4">
      <HowThisWorks lines={HOW_THIS_WORKS} />
      <div className="study-actions"><button type="button" className="btn btn-primary study-primary" data-testid="how-start" onClick={startItems}>Start</button></div>
    </StudyPage>
  );

  if (phase === 'post') return (
    <StudyPage title="Last questions" progress="Step 4 of 4 · page 1 of 2 · about 3 minutes" formTestId="post-form" submitTestId="post-submit" primaryLabel="Continue" canSubmit={!postMissing} remaining={postMissing} onSubmit={submitPost}>
      {postItems.map((it, n) => it.kind === 'choice' ? (
        <RadioQuestion key={it.id} name={it.id} number={n + 1} text={it.text} testId={it.testId} options={it.options.map(([key, label]) => ({ key, label }))} value={post[it.id]} onChange={v => setPost(p => ({ ...p, [it.id]: v }))}>
          {it.other && post[it.id] === 'other' && <input type="text" name={it.other} className="study-text" placeholder="Please describe" value={post[it.other] || ''} onChange={e => setPost(p => ({ ...p, [it.other!]: e.target.value }))} />}
        </RadioQuestion>
      ) : (
        <ScaleQuestion key={it.id} name={it.id} number={n + 1} text={it.text} testId={it.testId} min={it.min} max={it.max} labels={it.labels} minLabel={it.minLabel} maxLabel={it.maxLabel} value={post[it.id] ? Number(post[it.id]) : undefined} onChange={v => setPost(p => ({ ...p, [it.id]: v }))} />
      ))}
      <div className="study-card study-question"><h3 className="study-q">{postItems.length + 1}. What do you think this study was testing? (optional)</h3>
        <textarea name="demandText" className="study-text" rows={3} value={post.demandText || ''} onChange={e => setPost(p => ({ ...p, demandText: e.target.value }))} /></div>
    </StudyPage>
  );

  if (phase === 'recheck') return (
    <StudyPage title="Looking back at the requests" progress="Step 4 of 4 · page 2 of 2 · about 2 minutes" formTestId="recheck-form" submitTestId="recheck-submit" primaryLabel="Finish"
      lead="These are requests from messages you just handled. For each one, say whether it should have gone ahead, given your job and what you did this week. This is not about what you clicked; answer as you see it now."
      canSubmit={!recheckMissing} remaining={recheckMissing} onSubmit={submitRecheck}>
      <details className="study-card recheck-card" data-testid="recheck-card" onToggle={e => onRecheckCard((e.currentTarget as HTMLDetailsElement).open)}>
        <summary><b>My job and systems</b> (open if you want to check)</summary>
        <CardView />
      </details>
      {recheckOrder.map((id, n) => { const v = assignment.items.find(x => x.variantId === id)!; return (
        <RadioQuestion key={id} name={`recheck_${id}`} number={n + 1} testId="recheck-item"
          text={<><span className="recheck-line">{RECHECK_LINES[v.familyId][v.classId as 'A' | 'L']}</span><span className="study-hint"> {RECHECK_QUESTION}</span></>}
          options={RECHECK_OPTIONS.map(([key, label]) => ({ key, label }))} value={recheckAns[id]} onChange={a => setRecheckAns(r => ({ ...r, [id]: a }))} />
      ); })}
    </StudyPage>
  );

  if (phase === 'finish') return (
    <StudyPage title="Finishing up…" progress="Almost done">
      <StudyCard>
        {finishing && <p>Saving your responses.</p>}
        {finishError && <><p className="study-error" data-testid="finish-error">{FINISH_ERROR_TEXT}</p>
          <div className="study-actions"><button type="button" className="btn btn-primary study-primary" data-testid="finish-retry" onClick={finish}>Try again</button></div></>}
      </StudyCard>
    </StudyPage>
  );

  if (phase === 'debrief') return <DebriefPage arm="main" iface={iface} onShown={debriefShown} onAcknowledge={debriefAck} />;

  if (!current || !family || !viewing) return null;
  const cons = consequenceFor(current);
  const senderOk = isUniversity(current.senderAddress); const linkOk = isUniversity(current.linkUrl);
  const verified = senderOk && linkOk;
  const finishedThis = !!t.current?.finalAction || view === 'confidence';
  const arrived = assignment.items.slice(0, index + 1).reverse();
  const items: ShellItem[] = arrived.map(v => ({
    id: v.variantId, sender: v.senderName, subject: v.subject, preview: previewFor(v), time: timeFor(v),
    unread: v.variantId === current.variantId && !finishedThis, decided: v.variantId !== current.variantId || finishedThis,
  }));
  const clock = clockFor(current);
  const vclock = clockFor(viewing);
  const timeLine = vclock.weekday ? `${vclock.weekday} ${timeFor(viewing)}` : timeFor(viewing);
  const inLayer = view === 'guided' || view === 'destination' || view === 'verify';
  const commitGated = (iface === 'action' && intentAnswer !== 'yes') || (asksIdentity(iface) && identityAnswer !== 'yes');

  const destinationPage = (
    <div className="main-dest" data-testid="destination">
      <div className="main-dest-page">
        <h2 className="main-dest-title">{family.name}</h2>
        <div className="main-reveal">
          <button type="button" className="main-reveal-toggle" data-testid="reveal-toggle" onClick={toggleReveal} aria-expanded={revealOpen}>
            <span>What this will do</span><span className="chev" aria-hidden="true">{revealOpen ? '▾' : '▸'}</span>
          </button>
          {revealOpen && (
            <div className="main-reveal-body" data-testid="reveal-body" onMouseEnter={() => { timers.current.consStart = Date.now(); }} onMouseLeave={() => closeCons(Date.now())}>
              <div><span className="cal-k">What</span><span>{cons.what}</span></div><div><span className="cal-k">For</span><span>{cons.forWhom}</span></div>
              <div><span className="cal-k">Allows</span><span>{cons.allows}</span></div><div><span className="cal-k">Then</span><span>{cons.happens}</span></div>
            </div>
          )}
        </div>
        {iface !== 'ordinary' && (
          <p className="main-step-results" data-testid="step-results">
            Sender address: university address — <b>{senderOk ? 'Yes' : 'No'}</b> · Link destination: university address — <b>{linkOk ? 'Yes' : 'No'}</b>
          </p>
        )}
        {iface === 'identity' && (
          <div className={`main-verdict ${verified ? 'ok' : 'warn'}`} data-testid="identity-result">{verified ? 'Verified ✓ — university address' : 'Not verified — outside the university'}</div>
        )}
        {asksIdentity(iface) && (
          <div className="main-intent main-identityq" data-testid="identity-question">
            <p>Based on these checks, is this message from the university?</p>
            <div className="main-intent-buttons">
              <button type="button" className="btn btn-secondary" data-testid="identity-yes" disabled={!!identityAnswer} onClick={() => answerIdentity('yes')}>Yes, continue</button>
              <button type="button" className="btn btn-secondary" data-testid="identity-no" disabled={!!identityAnswer} onClick={() => answerIdentity('no')}>No</button>
            </div>
          </div>
        )}
        {iface === 'action' && (
          <div className="main-intent" data-testid="intent">
            <p>{ACTION_QUESTION}</p>
            <div className="main-intent-buttons">
              <button type="button" className="btn btn-secondary" data-testid="intent-yes" disabled={!!intentAnswer} onClick={() => answerIntent('yes')}>Yes, continue</button>
              <button type="button" className="btn btn-secondary" data-testid="intent-no" disabled={!!intentAnswer} onClick={() => answerIntent('no')}>No</button>
            </div>
          </div>
        )}
        <button type="button" className="btn btn-primary main-commit" data-testid="commit" disabled={commitGated} onClick={() => finalize('commit')}>{family.commitLabel}</button>
      </div>
    </div>
  );

  const guidedCard = (
    <div className="main-guided" data-testid="guided">
      <h3>Checking this link — step {guidedStep} of 2</h3>
      <div className="lbl">{guidedStep === 1 ? 'Sender address' : 'Link destination'}</div>
      <code>{guidedStep === 1 ? current.senderAddress : current.linkUrl}</code>
      <p>Is this a university address? (university.edu or something ending in .university.edu)</p>
      <div className="main-intent-buttons">
        <button type="button" className="btn btn-secondary" data-testid="step-yes" onClick={() => answerStep('yes')}>Yes</button>
        <button type="button" className="btn btn-secondary" data-testid="step-no" onClick={() => answerStep('no')}>No</button>
      </div>
    </div>
  );

  const vlabel = family.verifyChannel.label;
  const vkind: 'phone' | 'call' | 'text' | 'browser' = /^Open the Sign-in app/i.test(vlabel) ? 'phone' : /^Call /i.test(vlabel) ? 'call' : /^Text /i.test(vlabel) ? 'text' : 'browser';
  const vhost = (vlabel.match(/^Type (\S+)/i) || [])[1] || '';
  const verifyLayer = (
    <BrowserWindow variant={vkind} url={vhost ? `https://${vhost}/` : ''} title={vhost || vlabel} secure={!!vhost}>
      <div className="main-verify" data-testid="verify-channel">
        <VerifyChannel label={vlabel} reply={family.verifyChannel[current.classId as 'S' | 'A' | 'L']} />
        <div className="vc-actions"><button type="button" className="btn btn-primary" data-testid="verify-done" onClick={closeVerify}>Back to the message</button></div>
      </div>
    </BrowserWindow>
  );

  return (
    <>
      <OutlookShell
        decided={index + (finishedThis ? 1 : 0)} total={assignment.items.length} unreadCount={finishedThis ? 0 : 1}
        items={items} selectedId={viewing.variantId} onSelect={selectMessage}
        cardOpen={cardOpen} onToggleCard={toggleCard} cardPanel={<CardPanel onClose={toggleCard} />}
        scenarioClock={`${clock.clockLabel} · ${clock.placeLabel}`} handled={!!viewingId}
        statusText={inLayer ? null : hoverUrl} toast={toast} onDisabledAction={() => showToast('Not available in this study')}
        layer={inLayer} onPaneScroll={onScroll}
        decisionBar={view === 'email' && !viewingId ? (
          <EmailDecisionBar onDecision={d => {
            if (view !== 'email' || viewingId) return;
            if (d === 'engage') { const link = document.querySelector('.email-detail .email-body .email-link') as HTMLElement | null; link?.click(); return; }
            if (d === 'verify') { openVerify(); return; }
            finalize(d);
          }} currentDecision={null} disabled={false} />
        ) : null}
      >
        {view === 'guided' || view === 'destination' ? (
          <BrowserWindow url={current.linkUrl} title={hostOf(current.linkUrl)} secure={linkOk} onBack={backToInbox}>
            {view === 'guided' ? guidedCard : destinationPage}
          </BrowserWindow>
        ) : view === 'verify' ? verifyLayer : (
          <EmailMessage subject={viewing.subject} senderName={viewing.senderName} senderAddress={viewing.senderAddress} timeLine={timeLine} html={fullContent}
            onLinkEnter={onLinkEnter} onLinkLeave={onLinkLeave} onLinkClick={onLinkClick} />
        )}
      </OutlookShell>

      {view === 'confidence' && (
        <StudyModal title="How sure are you about what you just did?" width={600} stage="confidence" formTestId="confidence-form" submitTestId="confidence-submit"
          primaryLabel={index + 1 >= assignment.items.length ? 'Continue' : 'Next message'} canSubmit={confTouched} hint="Move the slider to continue" onSubmit={submitConfidence}>
          <ConfidenceSlider value={confidence} touched={confTouched} onChange={v => { setConfidence(v); setConfTouched(true); }} />
        </StudyModal>
      )}

      <SaveToast state={saveState} />
    </>
  );
};

export default MainTask;
